import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useParams } from "react-router-dom";
import { getParticipantRoles, getProjectUsers } from "../../../api/project";
import { updatePaperFolderPermissions, updatePaperFilePermissions } from "../../../api/papers";
import Modal from "../ui/Modal";

export default function FolderPermissionDialog({
    open,
    onOpenChange,
    folder, // Legacy prop
    file,   // New prop
    target, // Future prop
    type = "folder", // "folder" | "file"
    onUpdate,
}) {
    const { id: projectId } = useParams();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [allRoles, setAllRoles] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    // Actual target object
    const realTarget = file || folder || target;
    const realType = file ? "file" : type;

    // 선택된 상태
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    useEffect(() => {
        if (open && projectId) {
            setLoading(true);
            Promise.all([getParticipantRoles(projectId), getProjectUsers(projectId)])
                .then(([rolesRes, usersRes]) => {
                    const rolesData = rolesRes.data?.results || rolesRes.data || [];
                    setAllRoles(Array.isArray(rolesData) ? rolesData : []);
                    setAllUsers(usersRes.data || []);
                })
                .finally(() => setLoading(false));
        }
    }, [open, projectId]);

    useEffect(() => {
        if (realTarget && open) {
            // 초기값 세팅
            setSelectedRoles(realTarget.allowed_roles || []);
            setSelectedUsers(realTarget.allowed_users || []);
        }
    }, [realTarget, open]);

    const handleSubmit = async () => {
        if (!realTarget) return;
        setSubmitting(true);
        try {
            if (realType === "file") {
                await updatePaperFilePermissions(realTarget.id, {
                    allowed_roles: selectedRoles,
                    allowed_users: selectedUsers,
                });
            } else {
                await updatePaperFolderPermissions(realTarget.id, {
                    allowed_roles: selectedRoles,
                    allowed_users: selectedUsers,
                });
            }

            // 성공 시
            onUpdate?.(); // 트리 리로드 요청 등
            onOpenChange(false);
        } catch (e) {
            console.error(e);
            alert("권한 업데이트 실패");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleRole = (roleId) => {
        setSelectedRoles((prev) =>
            prev.includes(roleId)
                ? prev.filter((id) => id !== roleId)
                : [...prev, roleId]
        );
    };

    const toggleUser = (userId) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    if (!realTarget) return null;

    return (
        <Modal
            open={open}
            title={`${realType === "file" ? "파일" : "폴더"} 권한 설정 : ${realTarget.name || realTarget.title}`}
            onClose={() => onOpenChange(false)}
            width={500}
        >
            <div className="flex flex-col gap-6">
                {/* 1. 소유자 정보 (읽기 전용) - 폴더인 경우만 표시 */}
                {realType === "folder" && (
                    <div>
                        <h4 className="text-sm font-semibold text-slate-800 mb-2">
                            소유 역할 (Owner)
                        </h4>
                        {realTarget.owner_role ? (
                            <div className="text-sm text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-md inline-block font-medium">
                                {allRoles.find((r) => r.id === realTarget.owner_role)?.name ||
                                    "로딩 중..."}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-md inline-block">
                                공용 폴더 (소유자 없음)
                            </div>
                        )}
                    </div>
                )}

                {/* 2. 역할별 공유 */}
                <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">
                        참여사별 공유 허용
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                        {allRoles.map((role) => {
                            const isOwner = (realType === "folder" && role.id === realTarget.owner_role);
                            const isSelected = selectedRoles.includes(role.id);
                            return (
                                <label
                                    key={role.id}
                                    className={`flex items-center gap-2 p-2 rounded border text-sm cursor-pointer select-none
                    ${isOwner
                                            ? "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed"
                                            : isSelected
                                                ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                                                : "hover:bg-slate-50 border-slate-200 text-slate-600"
                                        }
                  `}
                                >
                                    <div
                                        className={`w-4 h-4 rounded border flex items-center justify-center
                      ${isSelected || isOwner
                                                ? "bg-indigo-600 border-indigo-600 text-white"
                                                : "bg-white border-slate-300"
                                            }
                    `}
                                        onClick={(e) => {
                                            if (isOwner) return;
                                            e.preventDefault();
                                            toggleRole(role.id);
                                        }}
                                    >
                                        {(isSelected || isOwner) && <Check className="w-3 h-3" />}
                                    </div>
                                    <span>{role.name}</span>
                                    {isOwner && <span className="text-xs text-slate-400">(소유)</span>}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* 3. 사용자별 공유 (파일만 가능) */}
                {realType === "file" && (
                    <div>
                        <h4 className="text-sm font-semibold text-slate-800 mb-2">
                            사용자별 공유 허용
                        </h4>
                        <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto divide-y divide-slate-100">
                            {allUsers.map((user) => {
                                const isSelected = selectedUsers.includes(user.id);
                                return (
                                    <div
                                        key={user.id}
                                        onClick={() => toggleUser(user.id)}
                                        className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50
                    ${isSelected ? "bg-indigo-50/50" : ""}
                  `}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded border flex items-center justify-center
                          ${isSelected
                                                        ? "bg-indigo-600 border-indigo-600 text-white"
                                                        : "bg-white border-slate-300"
                                                    }
                        `}
                                            >
                                                {isSelected && <Check className="w-3 h-3" />}
                                            </div>
                                            <span className={isSelected ? "text-indigo-700 font-medium" : "text-slate-600"}>
                                                {`${user.last_name}${user.first_name} (${user.company})`}
                                            </span>
                                        </div>
                                        <div>
                                            {/* 소속 회사 표시 등 가능 */}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-2">
                    <button
                        onClick={() => onOpenChange(false)}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                        {submitting ? "저장 중..." : "저장"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
